import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { TaxProfileInput } from './types';

export const taxProfileKeys = {
  all: ['tax-profiles'] as const,
  lists: () => [...taxProfileKeys.all, 'list'] as const,
  list: (p: api.TaxProfileListParams) => [...taxProfileKeys.lists(), p] as const,
};

export function useTaxProfiles(params: api.TaxProfileListParams = {}) {
  return useQuery({
    queryKey: taxProfileKeys.list(params),
    queryFn: () => api.listTaxProfiles(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateTaxProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaxProfileInput) => api.createTaxProfile(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taxProfileKeys.lists() });
      toast.success('Tax profile saved');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateTaxProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<TaxProfileInput> }) =>
      api.updateTaxProfile(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: taxProfileKeys.lists() }),
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteTaxProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteTaxProfile(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taxProfileKeys.lists() });
      toast.success('Tax profile deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
